/**
 * useDateRangePicker Hook
 *
 * State machine for Airbnb-style date range selection.
 * Phases: selecting-checkin → selecting-checkout → complete
 */

import { useState, useCallback, useMemo } from 'react';
import { isSameDay, isPast, formatDate } from '../../utils/dates';

export type SelectionPhase = 'selecting-checkin' | 'selecting-checkout' | 'complete';

export type DayStatus =
  | 'normal'
  | 'disabled'
  | 'blocked'
  | 'check-in'
  | 'check-out'
  | 'in-range'
  | 'hover-preview';

interface UseDateRangePickerOptions {
  blockedDates?: Set<string>;
  minimumStay?: number;
  maximumStay?: number;
  initialCheckIn?: Date | null;
  initialCheckOut?: Date | null;
  onChange?: (checkIn: Date | null, checkOut: Date | null) => void;
}

interface UseDateRangePickerReturn {
  checkIn: Date | null;
  checkOut: Date | null;
  phase: SelectionPhase;
  hoveredDate: Date | null;
  handleDayClick: (date: Date) => void;
  handleDayHover: (date: Date | null) => void;
  getDayStatus: (date: Date) => DayStatus;
  clearDates: () => void;
}

function toDateString(date: Date): string {
  return formatDate(date, 'numeric');
}

function hasBlockedDateInRange(
  start: Date,
  end: Date,
  blockedDates: Set<string>
): boolean {
  const current = new Date(start);
  current.setDate(current.getDate() + 1);
  while (current < end) {
    if (blockedDates.has(toDateString(current))) {
      return true;
    }
    current.setDate(current.getDate() + 1);
  }
  return false;
}

export function useDateRangePicker(
  options: UseDateRangePickerOptions = {}
): UseDateRangePickerReturn {
  const {
    blockedDates = new Set<string>(),
    minimumStay = 1,
    maximumStay,
    initialCheckIn = null,
    initialCheckOut = null,
    onChange,
  } = options;

  const [checkIn, setCheckIn] = useState<Date | null>(initialCheckIn);
  const [checkOut, setCheckOut] = useState<Date | null>(initialCheckOut);
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);

  const phase: SelectionPhase = useMemo(() => {
    if (!checkIn) return 'selecting-checkin';
    if (!checkOut) return 'selecting-checkout';
    return 'complete';
  }, [checkIn, checkOut]);

  const isDateDisabled = useCallback(
    (date: Date): boolean => {
      if (isPast(date)) return true;
      if (blockedDates.has(toDateString(date))) return true;
      return false;
    },
    [blockedDates]
  );

  const handleDayClick = useCallback(
    (date: Date) => {
      if (isDateDisabled(date)) return;

      if (phase === 'selecting-checkin' || phase === 'complete') {
        // Start fresh selection
        setCheckIn(date);
        setCheckOut(null);
        setHoveredDate(null);
        onChange?.(date, null);
      } else if (phase === 'selecting-checkout') {
        // Clicking before check-in restarts selection
        if (date <= checkIn!) {
          setCheckIn(date);
          setCheckOut(null);
          setHoveredDate(null);
          onChange?.(date, null);
          return;
        }

        const nights = Math.ceil(
          (date.getTime() - checkIn!.getTime()) / (1000 * 60 * 60 * 24)
        );

        // Enforce minimum stay
        if (nights < minimumStay) return;

        // Enforce maximum stay
        if (maximumStay && nights > maximumStay) return;

        // Check for blocked dates in range
        if (hasBlockedDateInRange(checkIn!, date, blockedDates)) return;

        setCheckOut(date);
        setHoveredDate(null);
        onChange?.(checkIn, date);
      }
    },
    [phase, checkIn, blockedDates, minimumStay, maximumStay, onChange, isDateDisabled]
  );

  const handleDayHover = useCallback(
    (date: Date | null) => {
      if (phase === 'selecting-checkout') {
        setHoveredDate(date);
      }
    },
    [phase]
  );

  const getDayStatus = useCallback(
    (date: Date): DayStatus => {
      const dateStr = toDateString(date);

      // Disabled (past)
      if (isPast(date)) return 'disabled';

      // Blocked
      if (blockedDates.has(dateStr)) return 'blocked';

      // Check-in selected
      if (checkIn && isSameDay(date, checkIn)) return 'check-in';

      // Check-out selected
      if (checkOut && isSameDay(date, checkOut)) return 'check-out';

      // In confirmed range
      if (checkIn && checkOut && date > checkIn && date < checkOut) {
        return 'in-range';
      }

      // Hover preview range
      if (
        phase === 'selecting-checkout' &&
        checkIn &&
        hoveredDate &&
        hoveredDate > checkIn &&
        date > checkIn &&
        date <= hoveredDate
      ) {
        // Only show preview if no blocked dates in range
        if (!hasBlockedDateInRange(checkIn, hoveredDate, blockedDates)) {
          const nights = Math.ceil(
            (hoveredDate.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24)
          );
          if (nights >= minimumStay && (!maximumStay || nights <= maximumStay)) {
            return 'hover-preview';
          }
        }
      }

      return 'normal';
    },
    [checkIn, checkOut, hoveredDate, phase, blockedDates, minimumStay, maximumStay]
  );

  const clearDates = useCallback(() => {
    setCheckIn(null);
    setCheckOut(null);
    setHoveredDate(null);
    onChange?.(null, null);
  }, [onChange]);

  return {
    checkIn,
    checkOut,
    phase,
    hoveredDate,
    handleDayClick,
    handleDayHover,
    getDayStatus,
    clearDates,
  };
}
